<template>
  <Page
    :loading="loading"
    :title="'Settings'"
    help-link="https://www.mailslurp.com/support/managing-subscription/"
  >
    <CardGrouping
      class="row"
      :data-external-plan-id="
        subscription ? subscription.externalPlanId : undefined
      "
      :data-payment-failure-count="
        paymentFailures ? paymentFailures.length.toString() : '0'
      "
      :data-has-payment-failure="
        paymentFailures && paymentFailures.length ? '1' : '0'
      "
      :data-user-frozen="user && user.isFrozen ? '1' : '0'"
      :data-id-no-cancel="!subscription || !subscription.cancelAt"
      :data-trial-days="subscriptionTrialDays.toString()"
      :data-loading="loading ? '1' : '0'"
      :data-has-no-subscription="hasNoSub ? '1' : '0'"
      data-id="settings-page"
    >
      <Account @grant="onGrantAccess" />

      <CardSection v-if="!isChild && !isVendor" title="Billing">
        <Card no-body data-id="plan-card">
          <Tabs data-id="settings-account-tabs">
            <Tab title="Plan" no-body data-id="settings-tab">
              <PlanType no-card />
            </Tab>
            <Tab
              v-if="subscription && !isChild"
              title="Limits"
              data-id="limits-tab"
            >
              <UserSubscriptionLimits
                v-if="userLimits !== null"
                :subscription="subscription"
                :user-limits="userLimits"
              />
            </Tab>
            <Tab
              test-id="invoice-list"
              data-id="invoice-list"
              :data-invoice-count="invoices ? invoices.length : 0"
              no-body
              :title="'Invoices'"
            >
              <DataTable
                data-id="invoices-table"
                :disable-delete="true"
                :disable-edit="isChild || isVendor"
                :disable-new="true"
                :fields="fieldsInvoices"
                :items="invoices || []"
                :loading="loading"
                icon="invoices"
                no-card
                disable-sort
                title="Invoices"
                @edit="onEditInvoice"
                @refresh="refreshInvoices"
              />
            </Tab>
            <Tab no-body test-id="quotes-tab" :title="'Quotes'">
              <QuoteTable no-card />
            </Tab>
          </Tabs>
        </Card>
      </CardSection>
      <CardSection v-if="!isChild && !isVendor" title="Data">
        <Card no-body>
          <Tabs>
            <Tab no-body :title="'Export data'">
              <ExportCard no-card />
            </Tab>
            <Tab no-body :title="'Zapier'">
              <ZapierCard no-card />
            </Tab>
          </Tabs>
        </Card>
      </CardSection>
    </CardGrouping>
  </Page>
</template>

<script lang="ts" setup>
import {
  type GrantAdminAccessToUserIdRequest,
  type InvoiceDto,
} from '@mailslurp/admin-sdk';
import Page from '../../components/Page.vue';
import Account from '../../components/Account.vue';
import PlanType from '../../components/PlanType.vue';
import ExportCard from '../../components/card/ExportCard.vue';
import ZapierCard from '../../components/card/ZapierCard.vue';
import {
  loadFetch,
  loadFetchMany,
} from '../../lib/services/helpers';
import Tabs from '../../components/form/Tabs.vue';
import DataTable from '../../components/table/parts/DataTable.vue';
import { listInvoices } from '../../lib/services/fields';
import Card from '../../components/tw/Card.vue';
import { computed, onMounted, ref } from 'vue';
import { useStore3 } from '../../store/main';
import { storeToRefs } from 'pinia';
import { type Types, Variant } from '../../store/types';
import Tab from '../../components/form/Tab.vue';
import UserSubscriptionLimits from '../../components/form/UserSubscriptionLimits.vue';
import QuoteTable from '../../components/table/QuoteTable.vue';
import CardSection from '../../components/tw/CardSection.vue';
import CardGrouping from '../../components/tw/CardGrouping.vue';
import { useRoute } from '#imports';
import { getLogger } from '../../lib/services/getLogger';

const store3 = useStore3();
const route = useRoute();

const log = getLogger('settings/index.vue');

const loading = ref<boolean>(false);
const fieldsInvoices = ref(listInvoices);

const subscriptionTrialDays = computed(() => {
  return (subscription.value?.trialDays ?? 0).toString();
});
const {
  subscription,
  userLimits,
  paymentFailures,
  user,
  invoices,
  isVendor,
  isChild,
  billingPortal,
} = storeToRefs(store3);

const hasNoSub = computed(() => {
  return !subscription.value;
});

onMounted(async () => {
  const { openBilling } = route.query ?? {};
  if (openBilling) {
    log('Loading billing portal');
    await loadFetch(() => store3.getBillingPortal(), store3, loading);
    if (billingPortal.value && billingPortal.value.url) {
      window.location.href = billingPortal.value.url;
    }
  }

  await loadFetchMany(
    [() => store3.getSubscription(), () => store3.getUserLimits()],
    store3,
    loading,
  );

  const sub = subscription.value;
  if (sub && sub.demoDaysRemaining == null && !isChild.value) {
    await loadFetchMany(
      [
        () => store3.getBillingPortal(),
        () => store3.getInvoices(),
        () => store3.getPaymentFailures(),
      ],
      store3,
      loading,
    );
  }
});

const onEditInvoice = (payload: { item: InvoiceDto }) => {
  const url: string = payload.item.url!;
  window.location.href = url;
};

const refreshInvoices = async () => {
  await loadFetch(() => store3.getInvoices(), store3, loading);
};

const onGrantAccess = async () => {
  const prompt = window.prompt(
    'Please tell support what they should do on your behalf.',
  );
  if (prompt) {
    const payload: GrantAdminAccessToUserIdRequest = {
      userId: user.value!.id,
      message: prompt,
    };
    await loadFetch(() => store3.grantAdminAccess(payload), store3, loading);
    const flash: Types = {
      variant: Variant.success,
      text: 'Access to your account has been temporarily given to support.',
    };
    store3.setFlash(flash);
  }
};
</script>
